import { type KeyboardEvent, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { Problem } from '../../api/model';
import { AddSubscriberForm } from './AddSubscriberForm';
import { EditSubscriberForm } from './EditSubscriberForm';
import { SubscriberDetailsCard } from './SubscriberDetailsCard';
import { useUISubscribersGetSubscriber } from '../../api/subscribers-ui/subscribers-ui.ts';

const getSubscriberFetchError = (error: Problem) => {
  if (!error) {
    return '';
  }
  if (error.status === 404) {
    return 'Subscriber not found';
  }
  if (error.status === 401) {
    return 'Unauthorized access';
  }
  return 'An error occurred while fetching subscriber data';
};

export const SubscriberPage = () => {
  const [username, setUsername] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const { data, error, isFetching, refetch, isLoading } = useUISubscribersGetSubscriber(searchQuery, {
    query: {
      enabled: Boolean(searchQuery),
    },
  });

  const handleSearchQuery = (newUsername?: string) => {
    if (searchQuery === newUsername) {
      void refetch();
    } else if (newUsername) {
      setUsername(newUsername);
      setSearchQuery(newUsername);
    } else if (searchQuery === username) {
      void refetch();
    } else {
      setSearchQuery(username);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearchQuery();
    }
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddSuccess = (newUsername: string) => {
    handleSearchQuery(newUsername);
    setOpenAddDialog(false);
  };

  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleEditSuccess = () => {
    handleSearchQuery();
    setOpenEditDialog(false);
  };

  const handleDeleteSuccess = () => {
    setUsername('');
    setSearchQuery('');
  };

  return (
    <Box
      sx={{
        maxWidth: 900,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Subscribers
        </Typography>
      </Stack>

      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
              <Box>
                <TextField
                  label="Search by Username"
                  placeholder="Enter subscriber username..."
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                  helperText="Press Enter to search"
                />
                <IconButton onClick={() => handleSearchQuery()} aria-label="search" color="primary">
                  <SearchIcon />
                </IconButton>
              </Box>
              <Button variant="contained" onClick={handleOpenAddDialog}>
                Add Subscriber
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {isLoading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && !isFetching && <Alert severity="error">{getSubscriberFetchError(error)}</Alert>}

        {!error && !isFetching && !data && (
          <Alert severity="info">Enter a username to search for subscriber details</Alert>
        )}

        {data && !error && !isLoading && (
          <SubscriberDetailsCard
            subscriber={data}
            onEdit={handleOpenEditDialog}
            onDeleteSuccess={handleDeleteSuccess}
          />
        )}
      </Box>

      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <AddSubscriberForm onSuccess={handleAddSuccess} onClose={handleCloseAddDialog} />
      </Dialog>

      {data && !error && (
        <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <EditSubscriberForm
            subscriber={data}
            onSuccess={handleEditSuccess}
            onClose={handleCloseEditDialog}
          />
        </Dialog>
      )}
    </Box>
  );
};
